import React, { useState, useRef } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'react-toastify';
import './style.css';

const AuraContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const recaptchaRef = useRef(null);

    // Get today's date in YYYY-MM-DD format for min date
    const today = new Date().toISOString().split('T')[0];

    // Validation functions
    const validateName = (name) => {
        if (!name) return 'Name is required';
        if (name.length < 2) return 'Name must be at least 2 characters';
        return '';
    };

    const validateEmail = (email) => {
        if (!email) return 'Email is required';
        if (!/\S+@\S+\.\S+/.test(email)) return 'Invalid email format';
        return '';
    };

    const validatePhone = (phone) => {
        const digits = phone.replace(/\D/g, '');
        if (!phone) return 'Phone number is required';
        if (digits.length < 8) return 'Enter a valid phone number';
        return '';
    };

    const validateDate = (date) => {
        if (!date) return 'Please select a date';
        if (date < today) return 'Please select a future date';
        return '';
    };

    const validateTime = (time) => {
        if (!time) return 'Please select a time';
        return '';
    };

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error on change
        if (errors[name]) {
            setErrors(prev => {
                const copy = { ...prev };
                delete copy[name];
                return copy;
            });
        }
    };

    // Handle phone change
    const handlePhoneChange = (phone) => {
        setFormData(prev => ({ ...prev, phone }));

        // Only validate if user has already touched the field
        if (touched.phone) {
            const error = validatePhone(phone);
            if (error) {
                setErrors(prev => ({ ...prev, phone: error }));
            } else {
                setErrors(prev => {
                    const copy = { ...prev };
                    delete copy.phone;
                    return copy;
                });
            }
        }
    };

    // Handle blur validation
    const handleBlur = (e) => {
        const { name, value } = e.target;

        // Mark field as touched
        setTouched(prev => ({ ...prev, [name]: true }));

        // Only validate if field has a value (user entered something)
        if (!value) return;

        let error = '';

        switch (name) {
            case 'name':
                error = validateName(value);
                break;
            case 'email':
                error = validateEmail(value);
                break;
            case 'date':
                error = validateDate(value);
                break;
            case 'time':
                error = validateTime(value);
                break;
            default:
                break;
        }

        if (error) {
            setErrors(prev => ({ ...prev, [name]: error }));
        } else {
            setErrors(prev => {
                const copy = { ...prev };
                delete copy[name];
                return copy;
            });
        }
    };

    // Handle phone blur
    const handlePhoneBlur = () => {
        // Mark phone as touched
        setTouched(prev => ({ ...prev, phone: true }));

        // Get just the digits to check if user entered more than country code
        const digits = formData.phone.replace(/\D/g, '');

        // Only validate if user has entered digits beyond initial country code (usually 1-3 digits)
        if (digits.length <= 3) return;

        const error = validatePhone(formData.phone);
        if (error) {
            setErrors(prev => ({ ...prev, phone: error }));
        } else {
            setErrors(prev => {
                const copy = { ...prev };
                delete copy.phone;
                return copy;
            });
        }
    };

    // Validate entire form
    const validateForm = () => {
        const newErrors = {};

        const nameError = validateName(formData.name);
        if (nameError) newErrors.name = nameError;

        const emailError = validateEmail(formData.email);
        if (emailError) newErrors.email = emailError;

        const phoneError = validatePhone(formData.phone);
        if (phoneError) newErrors.phone = phoneError;

        const dateError = validateDate(formData.date);
        if (dateError) newErrors.date = dateError;

        const timeError = validateTime(formData.time);
        if (timeError) newErrors.time = timeError;

        if (!captchaToken) {
            newErrors.captcha = 'Please complete the reCAPTCHA';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the highlighted errors before submitting.');
            return;
        }

        setIsLoading(true);

        const submitData = {
            Name: formData.name,
            Email: formData.email,
            Phone: formData.phone,
            PreferredDate: formData.date,
            PreferredTime: formData.time,
            SheetName: 'AuraClinic',
        };

        const scriptURL = process.env.REACT_APP_GSHEET_URL;

        try {
            await fetch(scriptURL, {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(submitData).toString(),
            });

            toast.success('Thank you! We will contact you soon.');

            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                date: '',
                time: '',
            });
            setErrors({});
            setTouched({});
            setCaptchaToken('');
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
        } catch (error) {
            console.error('Submission failed:', error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <section className="aura-contact-form-section">
            <div className="container">
                <div className="row align-items-center">
                    <div className="col-lg-5">
                        <div className="aura-contact-form-content">
                            <h2 className="aura-contact-form-heading">
                                Get in Touch
                            </h2>
                            <p className="description-text mt-0">
                                Ready to transform your clinic operations? Fill out the form and our team will reach out to schedule a personalised demo at your preferred time.
                            </p>
                            <div className="aura-contact-features">
                                <div className="aura-contact-feature">
                                    <i className="fa-solid fa-check-circle"></i>
                                    <span>Free consultation</span>
                                </div>
                                <div className="aura-contact-feature">
                                    <i className="fa-solid fa-check-circle"></i>
                                    <span>Personalised demo</span>
                                </div>
                                <div className="aura-contact-feature">
                                    <i className="fa-solid fa-check-circle"></i>
                                    <span>No commitment required</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-7">
                        <div className="aura-contact-form-card">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="aura-form-group">
                                            <label htmlFor="name">Full Name</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                placeholder="Enter your name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={errors.name ? 'aura-input-error' : ''}
                                            />
                                            {errors.name && (
                                                <p className="aura-error-text">{errors.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="aura-form-group">
                                            <label htmlFor="email">Email Address</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder="Enter your email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={errors.email ? 'aura-input-error' : ''}
                                            />
                                            {errors.email && (
                                                <p className="aura-error-text">{errors.email}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="aura-form-group">
                                            <label htmlFor="phone">Phone Number</label>
                                            <PhoneInput
                                                defaultCountry="us"
                                                placeholder="Enter phone number"
                                                value={formData.phone}
                                                onChange={handlePhoneChange}
                                                onBlur={handlePhoneBlur}
                                                className={`aura-phone-input ${errors.phone ? 'aura-input-error' : ''}`}
                                            />
                                            {errors.phone && (
                                                <p className="aura-error-text">{errors.phone}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="aura-form-group">
                                            <label htmlFor="date">Preferred Date</label>
                                            <input
                                                type="date"
                                                id="date"
                                                name="date"
                                                min={today}
                                                value={formData.date}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={errors.date ? 'aura-input-error' : ''}
                                            />
                                            {errors.date && (
                                                <p className="aura-error-text">{errors.date}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="aura-form-group">
                                            <label htmlFor="time">Preferred Time</label>
                                            <input
                                                type="time"
                                                id="time"
                                                name="time"
                                                value={formData.time}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                className={errors.time ? 'aura-input-error' : ''}
                                            />
                                            {errors.time && (
                                                <p className="aura-error-text">{errors.time}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="aura-form-group aura-recaptcha-wrapper">
                                            <ReCAPTCHA
                                                ref={recaptchaRef}
                                                sitekey="6LduktkqAAAAAAG-TJxQPKptt1p5F4l_FYqYqNgv"
                                                onChange={(token) => setCaptchaToken(token)}
                                            />
                                            {errors.captcha && (
                                                <p className="aura-error-text">{errors.captcha}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <button
                                            type="submit"
                                            className="aura-submit-button"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <span className="aura-spinner"></span>
                                                    Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    <span>Book a Demo</span>
                                                    <i className="fa-solid fa-arrow-right-long"></i>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AuraContactForm;
